import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Warning, DownloadSimple, Trash, Lock, CheckCircle } from "@phosphor-icons/react";
import { PassportLogo } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { accountApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const DELETES = [
    "Profile & authentication account", "Passport stamps & travel history",
    "Photos, videos, stories & reels", "Reviews, posts & comments",
    "Saved trips & bucket list", "Messages & conversations",
    "Followers & following", "Subscription (cancelled)",
    "Push tokens & analytics identifiers",
];

export default function DeleteAccount() {
    const { t } = useTranslation();
    const { user, signout } = useAuth() || {};
    const nav = useNavigate();

    // Public form state
    const [email, setEmail] = useState(user?.email || "");
    const [username, setUsername] = useState(user?.handle || "");
    const [reason, setReason] = useState("");
    const [understood, setUnderstood] = useState(false);
    const [password, setPassword] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const isLoggedIn = !!user;
    const needsPassword = isLoggedIn && user?.tier !== undefined; // authed path always offers password field

    const beginDelete = (e) => {
        e.preventDefault();
        if (!understood) return toast.error(t("deleteAccount.toast.confirmPermanent"));
        if (!isLoggedIn && !email) return toast.error(t("deleteAccount.toast.enterEmail"));
        setConfirmOpen(true);
    };

    const confirmDelete = async () => {
        setBusy(true);
        try {
            if (isLoggedIn) {
                await accountApi.deleteAccount({ password: password || null, reason, confirm: true });
                signout?.();
                setDone(true);
            } else {
                const r = await accountApi.publicDeleteRequest({ email, username: username || null, reason, confirm: true });
                setDone(true);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("deleteAccount.toast.error"));
            setConfirmOpen(false);
        } finally { setBusy(false); }
    };

    const downloadData = async () => {
        try {
            toast.message(t("deleteAccount.toast.preparingData"));
            const blob = await accountApi.exportData();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `passport-data-${user?.handle || "me"}.zip`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success(t("deleteAccount.toast.dataStarted"));
        } catch (e) {
            toast.error(t("deleteAccount.toast.dataError"));
        }
    };

    if (done) {
        return (
            <div className="min-h-screen grid place-items-center px-6 grain" data-testid="delete-done">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="max-w-lg w-full text-center rounded-3xl p-10"
                    style={{ background: "var(--navy-deep)", border: "1px solid rgba(212,175,55,0.25)" }}>
                    <div className="w-20 h-20 mx-auto mb-8 rounded-full grid place-items-center" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid var(--gold)" }}>
                        <CheckCircle size={40} weight="fill" color="#D4AF37" />
                    </div>
                    <div className="overline mb-4">{t("deleteAccount.done.requestComplete")}</div>
                    <h1 className="font-serif text-4xl text-white leading-tight mb-4">{t("deleteAccount.done.title")}</h1>
                    <p className="text-white/60 leading-relaxed mb-10">
                        {isLoggedIn
                            ? t("deleteAccount.done.loggedInDesc")
                            : t("deleteAccount.done.loggedOutDesc")}
                    </p>
                    <Link to="/" className="btn-gold" data-testid="delete-home">{t("deleteAccount.done.returnHome")}</Link>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen grain" data-testid="delete-account-page">
            <div className="max-w-2xl mx-auto px-6 md:px-10 py-12">
                <Link to={isLoggedIn ? "/app" : "/"} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[color:var(--gold)] mb-10" data-testid="delete-back">
                    <ArrowLeft size={14} /> {t("deleteAccount.back")}
                </Link>

                <div className="flex items-center gap-3 mb-8">
                    <PassportLogo size={40} />
                    <div className="overline">{t("deleteAccount.overline")}</div>
                </div>

                <h1 className="font-serif text-5xl md:text-6xl text-white tracking-tighter leading-[0.95] mb-4">{t("deleteAccount.title")}</h1>
                <p className="text-white/60 text-lg leading-relaxed mb-10">
                    {t("deleteAccount.subtitle")}
                </p>

                {/* Export first */}
                {isLoggedIn && (
                    <div className="rounded-2xl p-6 mb-6 flex items-center justify-between gap-4" style={{ background: "rgba(212,175,55,0.05)", border: "1px solid rgba(212,175,55,0.2)" }}>
                        <div>
                            <div className="font-serif text-xl text-white">{t("deleteAccount.export.title")}</div>
                            <div className="text-white/60 text-sm mt-1">{t("deleteAccount.export.desc")}</div>
                        </div>
                        <button onClick={downloadData} className="btn-ghost shrink-0" data-testid="delete-download-data">
                            <DownloadSimple size={16} /> {t("deleteAccount.export.button")}
                        </button>
                    </div>
                )}

                {/* What gets deleted */}
                <div className="rounded-2xl p-6 mb-8" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <div className="flex items-center gap-2 mb-4">
                        <Warning size={16} color="#D4AF37" weight="fill" />
                        <span className="overline">{t("deleteAccount.whatDeleted.title")}</span>
                    </div>
                    <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6">
                        {["profile", "stamps", "photos", "reviews", "trips", "messages", "followers", "subscription", "tokens"].map((k) => (
                            <li key={k} className="flex items-start gap-2 text-sm text-white/70">
                                <Trash size={12} className="mt-1 shrink-0" color="rgba(255,255,255,0.4)" /> {t(`deleteAccount.whatDeleted.items.${k}`)}
                            </li>
                        ))}
                    </ul>
                    <p className="text-white/40 text-xs mt-4 leading-relaxed">
                        {t("deleteAccount.whatDeleted.footer")}
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={beginDelete} className="space-y-5" data-testid="delete-form">
                    <div>
                        <span className="overline block mb-2">{t("deleteAccount.form.email")} {isLoggedIn && t("deleteAccount.form.yourAccount")}</span>
                        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isLoggedIn} placeholder={t("deleteAccount.form.emailPlaceholder")} className="input" data-testid="delete-email" />
                    </div>
                    <div>
                        <span className="overline block mb-2">{t("deleteAccount.form.username")}</span>
                        <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder={t("deleteAccount.form.usernamePlaceholder")} className="input" data-testid="delete-username" />
                    </div>
                    <div>
                        <span className="overline block mb-2">{t("deleteAccount.form.reason")}</span>
                        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder={t("deleteAccount.form.reasonPlaceholder")} className="input" data-testid="delete-reason" />
                    </div>
                    {isLoggedIn && (
                        <div>
                            <span className="overline mb-2 flex items-center gap-2"><Lock size={11} /> {t("deleteAccount.form.password")}</span>
                            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("deleteAccount.form.passwordPlaceholder")} className="input" data-testid="delete-password" />
                        </div>
                    )}

                    <label className="flex items-start gap-3 cursor-pointer py-2" data-testid="delete-understand-label">
                        <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} className="mt-1 w-4 h-4 accent-[#D4AF37]" data-testid="delete-understand" />
                        <span className="text-sm text-white/80">{t("deleteAccount.form.understandPrefix")} <strong className="text-white">{t("deleteAccount.form.permanent")}</strong> {t("deleteAccount.form.understandSuffix")}</span>
                    </label>

                    <button type="submit" disabled={!understood}
                        className="w-full justify-center rounded-full py-3.5 text-sm font-medium transition-colors flex items-center gap-2"
                        style={{ background: understood ? "#c0392b" : "rgba(192,57,43,0.3)", color: "white", cursor: understood ? "pointer" : "not-allowed" }}
                        data-testid="delete-submit">
                        <Trash size={16} /> {t("deleteAccount.form.submit")}
                    </button>
                </form>

                <div className="mt-8 text-center text-sm">
                    <Link to="/privacy" className="text-white/60 hover:text-[color:var(--gold)]">{t("deleteAccount.footer.privacyPolicy")}</Link>
                    <span className="mx-3 text-white/30">·</span>
                    <a href="mailto:privacy@passporttravelnetwork.com" className="text-white/60 hover:text-[color:var(--gold)]">{t("deleteAccount.footer.contactPrivacy")}</a>
                </div>
            </div>

            {/* Second confirmation dialog */}
            <AnimatePresence>
                {confirmOpen && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[90] flex items-center justify-center p-4"
                        style={{ background: "rgba(5,10,17,0.9)", backdropFilter: "blur(8px)" }}
                        data-testid="delete-confirm-dialog">
                        <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                            className="rounded-3xl max-w-md w-full p-8" style={{ background: "var(--navy)", border: "1px solid rgba(192,57,43,0.5)" }}>
                            <div className="w-14 h-14 rounded-full grid place-items-center mb-5" style={{ background: "rgba(192,57,43,0.15)", border: "1px solid rgba(192,57,43,0.6)" }}>
                                <Warning size={24} color="#e74c3c" weight="fill" />
                            </div>
                            <h3 className="font-serif text-3xl text-white leading-tight mb-4">{t("deleteAccount.confirmDialog.title")}</h3>
                            <p className="text-white/70 text-sm leading-relaxed mb-8">
                                {t("deleteAccount.confirmDialog.desc")}
                            </p>
                            <div className="flex gap-3">
                                <button onClick={() => setConfirmOpen(false)} disabled={busy} className="btn-ghost flex-1 justify-center" data-testid="delete-cancel">{t("deleteAccount.confirmDialog.keep")}</button>
                                <button onClick={confirmDelete} disabled={busy}
                                    className="flex-1 justify-center rounded-full py-3.5 text-sm font-medium flex items-center gap-2"
                                    style={{ background: "#c0392b", color: "white" }}
                                    data-testid="delete-confirm">
                                    {busy ? t("deleteAccount.confirmDialog.deleting") : t("deleteAccount.confirmDialog.submit")}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <style>{`
                .input { width: 100%; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 13px 15px; color: white; font-size: 14px; transition: border-color 0.3s ease; }
                .input::placeholder { color: rgba(255,255,255,0.35); }
                .input:focus { outline: none; border-color: var(--gold); }
                .input:disabled { opacity: 0.6; }
            `}</style>
        </div>
    );
}
